
import UserContext from "context/UserContext";
import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router";
import ApiService from "../../infrastructure/Services/ApiService";
import StorageService from '../../infrastructure/StorageService';
import DataService from '../../infrastructure/Services/DataService';
// reactstrap components
import {
    Card,
    CardHeader,
    CardBody,
    Input,
    Container,
    Row,
    Col,
    Button,
    FormGroup,
    Label,
    Collapse
} from "reactstrap";
import { toast } from 'react-toastify';

export default (() => {
    const [risks, setRisks] = useState([]);
    const [controlMeasures, setControlMeasures] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [expandedControlMeasures, setExpandedControlMeasures] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const user = useContext(UserContext);
    const history = useHistory();

    const getData = async () => {
        const template = await DataService.labBookings.riskTemplate(false);

        setRisks(template?.risks || []);
        setControlMeasures(template?.controlMeasures || []);
        // Expand first category by default
        if (template?.risks && template.risks.length > 0) {
            setExpandedCategories({ [template.risks[0].id]: true });
        }
    }

    useEffect(() => {
        if (!user.isAdmin) {
            history.location = "/dashboard";
            return;
        }
        getData();
    }, []);

    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const generateId = () => {
        var uid = crypto.randomUUID();
        return uid;
    };

    // Category operations
    const addCategory = () => {
        const newCategory = {
            id: generateId(),
            order: risks.length + 1,
            name: "New Category",
            hazards: []
        };
        setRisks([...risks, newCategory]);
        setExpandedCategories({ ...expandedCategories, [newCategory.id]: true });
    };

    const removeCategory = (categoryId) => {
        if (window.confirm('Are you sure you want to remove this category?')) {
            const newData = risks.filter(cat => cat.id !== categoryId);
            // Reorder remaining categories
            const reordered = newData.map((cat, index) => ({ ...cat, order: index + 1 }));
            setRisks(reordered);
            const { [categoryId]: removed, ...remaining } = expandedCategories;
            setExpandedCategories(remaining);
        }
    };

    const updateCategoryName = (categoryId, name) => {
        setRisks(risks.map(cat =>
            cat.id === categoryId ? { ...cat, name } : cat
        ));
    };

    const moveCategoryUp = (categoryId) => {
        const index = risks.findIndex(cat => cat.id === categoryId);
        if (index > 0) {
            const newData = [...risks];
            [newData[index - 1], newData[index]] = [newData[index], newData[index - 1]];
            // Update order numbers
            const reordered = newData.map((cat, idx) => ({ ...cat, order: idx + 1 }));
            setRisks(reordered);
        }
    };

    const moveCategoryDown = (categoryId) => {
        const index = risks.findIndex(cat => cat.id === categoryId);
        if (index < risks.length - 1) {
            const newData = [...risks];
            [newData[index], newData[index + 1]] = [newData[index + 1], newData[index]];
            // Update order numbers
            const reordered = newData.map((cat, idx) => ({ ...cat, order: idx + 1 }));
            setRisks(reordered);
        }
    };

    // Hazard operations
    const addHazard = (categoryId) => {
        setRisks(risks.map(cat => {
            if (cat.id === categoryId) {
                const newHazard = {
                    id: generateId(),
                    name: "New Hazard"
                };
                return { ...cat, hazards: [...cat.hazards, newHazard] };
            }
            return cat;
        }));
    };

    const removeHazard = (categoryId, hazardId) => {
        setRisks(risks.map(cat => {
            if (cat.id === categoryId) {
                return { ...cat, hazards: cat.hazards.filter(h => h.id !== hazardId) };
            }
            return cat;
        }));
    };

    const updateHazardName = (categoryId, hazardId, name) => {
        setRisks(risks.map(cat => {
            if (cat.id === categoryId) {
                return {
                    ...cat,
                    hazards: cat.hazards.map(h =>
                        h.id === hazardId ? { ...h, name } : h
                    )
                };
            }
            return cat;
        }));
    };

    // Control Measure operations (now at top level)
    const addControlMeasure = () => {
        const newMeasure = {
            id: generateId(),
            name: "New Control Measure",
            options: []
        };
        setControlMeasures([...controlMeasures, newMeasure]);
    };

    const removeControlMeasure = (measureId) => {
        if (window.confirm('Are you sure you want to remove this control measure?')) {
            setControlMeasures(controlMeasures.filter(m => m.id !== measureId));
        }
    };

    const updateControlMeasureName = (measureId, name) => {
        setControlMeasures(controlMeasures.map(m =>
            m.id === measureId ? { ...m, name } : m
        ));
    };

    // Option operations
    const addOption = (measureId) => {
        setControlMeasures(controlMeasures.map(m => {
            if (m.id === measureId) {
                const options = m.options || [];
                return { ...m, options: [...options, `Option ${options.length + 1}`] };
            }
            return m;
        }));
    };

    const removeOption = (measureId, optionIndex) => {
        setControlMeasures(controlMeasures.map(m => {
            if (m.id === measureId) {
                const options = m.options || [];
                return { ...m, options: options.filter((_, idx) => idx !== optionIndex) };
            }
            return m;
        }));
    };

    const updateOption = (measureId, optionIndex, value) => {
        setControlMeasures(controlMeasures.map(m => {
            if (m.id === measureId) {
                const options = [...(m.options || [])];
                options[optionIndex] = value;
                return { ...m, options };
            }
            return m;
        }));
    };

    const validate = () => {
        const errors = {};
        let isValid = true;

        risks.forEach(category => {
            const categoryErrors = {};

            if (!category.name || category.name.trim() === '') {
                categoryErrors.name = 'Category name is required';
                isValid = false;
            }

            if (Object.keys(categoryErrors).length > 0) {
                errors[category.id] = categoryErrors;
            }
        });

        setValidationErrors(errors);
        return isValid;
    };

    const save = async () => {
        const isValid = validate();
        if (!isValid) {
            toast.error('Please fix validation errors before saving');
            return;
        }

        try {
            const data = { risks, controlMeasures };
            ApiService.put('riskTemplate', { data: data }).then(async json => {
                toast.success("Risk Template Saved");

                await ApiService.fromStorage(`lab-bookings/risktemplatev2.json`).then((json) => {
                    StorageService.set(StorageService.keys.riskTemplate, json.riskTemplate);
                });

            });

            toast.success('Risk template saved successfully');
        } catch (error) {
            console.error('Error saving:', error);
            toast.error('Failed to save risk template');
        }
    };

    return <>
        <Container className="mt--7" fluid>
            <Row>
                <Col className="mb-5 mb-xl-0" xl="12">
                    <Card className="shadow">
                        <CardHeader className="bg-transparent">
                            <Row className="align-items-center">
                                <Col className="d-flex align-items-center justify-content-between">
                                    <h3 className="mb-0">Administration &gt; Risk Template</h3>
                                    <div>
                                        <Button color="primary" onClick={save}>
                                            <i className="fas fa-save"></i> Save
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </CardHeader>
                        <CardBody>
                            {/* Risk Categories Section */}
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h4 className="mb-0">Risk Categories</h4>
                                    <Button color="success" onClick={addCategory}>
                                        <i className="fas fa-plus"></i> Add Category
                                    </Button>
                                </div>
                                {risks && risks.length > 0 ? (
                                    <div>
                                        {risks.map((category, catIndex) => (
                                            <Card key={category.id} className="mb-3" style={{ border: '1px solid #dee2e6' }}>
                                                <CardHeader
                                                    style={{
                                                        cursor: 'pointer',
                                                        backgroundColor: '#f6f9fc',
                                                        borderBottom: expandedCategories[category.id] ? '1px solid #dee2e6' : 'none'
                                                    }}
                                                    onClick={() => toggleCategory(category.id)}
                                                >
                                                    <Row className="align-items-center">
                                                        <Col xs="auto">
                                                            <i className={`fas fa-chevron-${expandedCategories[category.id] ? 'down' : 'right'}`}></i>
                                                        </Col>
                                                        <Col>
                                                            <h4 className="mb-0">
                                                                {category.order}. {category.name}
                                                            </h4>
                                                        </Col>
                                                        <Col xs="auto">
                                                            <Button
                                                                color="secondary"
                                                                outline
                                                                onClick={(e) => { e.stopPropagation(); moveCategoryUp(category.id); }}
                                                                disabled={catIndex === 0}
                                                                className="mr-1"
                                                            >
                                                                <i className="fas fa-arrow-up"></i>
                                                            </Button>
                                                            <Button
                                                                color="secondary"
                                                                outline
                                                                onClick={(e) => { e.stopPropagation(); moveCategoryDown(category.id); }}
                                                                disabled={catIndex === risks.length - 1}
                                                                className="mr-1"
                                                            >
                                                                <i className="fas fa-arrow-down"></i>
                                                            </Button>
                                                            <Button
                                                                color="danger"
                                                                outline
                                                                onClick={(e) => { e.stopPropagation(); removeCategory(category.id); }}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </CardHeader>
                                                <Collapse isOpen={expandedCategories[category.id]}>
                                                    <CardBody>
                                                        {/* Category Name */}
                                                        <FormGroup>
                                                            <Label for={`category-name-${category.id}`}>
                                                                <strong>Category Name</strong>
                                                            </Label>
                                                            <Input
                                                                type="text"
                                                                id={`category-name-${category.id}`}
                                                                value={category.name}
                                                                onChange={(e) => updateCategoryName(category.id, e.target.value)}
                                                                invalid={validationErrors[category.id]?.name}
                                                            />
                                                            {validationErrors[category.id]?.name && (
                                                                <small className="text-danger">{validationErrors[category.id].name}</small>
                                                            )}
                                                        </FormGroup>

                                                        {/* Hazards Section */}
                                                        <div className="mt-4">
                                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                                <h5 className="mb-0">Hazards</h5>
                                                                <Button
                                                                    color="info"
                                                                    onClick={() => addHazard(category.id)}
                                                                >
                                                                    <i className="fas fa-plus"></i> Add Hazard
                                                                </Button>
                                                            </div>
                                                            {category.hazards && category.hazards.length > 0 ? (
                                                                <div className="pl-3">
                                                                    {category.hazards.map((hazard) => (
                                                                        <div key={hazard.id} className="mb-2">
                                                                            <Row>
                                                                                <Col xs="10">
                                                                                    <Input
                                                                                        type="text"
                                                                                        value={hazard.name}
                                                                                        onChange={(e) => updateHazardName(category.id, hazard.id, e.target.value)}
                                                                                        placeholder="Hazard name"
                                                                                    />
                                                                                </Col>
                                                                                <Col xs="2">
                                                                                    <Button
                                                                                        color="danger"
                                                                                        outline
                                                                                        onClick={() => removeHazard(category.id, hazard.id)}
                                                                                        block
                                                                                    >
                                                                                        <i className="fas fa-trash"></i>
                                                                                    </Button>
                                                                                </Col>
                                                                            </Row>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <p className="text-muted pl-3">No hazards defined</p>
                                                            )}
                                                        </div>
                                                    </CardBody>
                                                </Collapse>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-3">
                                        <p className="text-muted mb-3">No categories defined</p>
                                        <Button color="success" onClick={addCategory}>
                                            <i className="fas fa-plus"></i> Add First Category
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <hr className="my-4" />

                            {/* Control Measures Section (Shared) */}
                            <Card className="mb-3" style={{ border: '1px solid #dee2e6' }}>
                                <CardHeader
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: '#e9ecef',
                                        borderBottom: expandedControlMeasures ? '1px solid #dee2e6' : 'none'
                                    }}
                                    onClick={() => setExpandedControlMeasures(!expandedControlMeasures)}
                                >
                                    <Row className="align-items-center">
                                        <Col xs="auto">
                                            <i className={`fas fa-chevron-${expandedControlMeasures ? 'down' : 'right'}`}></i>
                                        </Col>
                                        <Col>
                                            <h4 className="mb-0">Control Measures (Shared across all risks)</h4>
                                        </Col>
                                        <Col xs="auto">
                                            <Button
                                                color="success"
                                                onClick={(e) => { e.stopPropagation(); addControlMeasure(); }}
                                            >
                                                <i className="fas fa-plus"></i> Add Control Measure
                                            </Button>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <Collapse isOpen={expandedControlMeasures}>
                                    <CardBody>
                                        {controlMeasures && controlMeasures.length > 0 ? (
                                            <div>
                                                {controlMeasures.map((measure) => (
                                                    <Card key={measure.id} className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                                                        <CardBody>
                                                            <Row className="mb-2">
                                                                <Col xs="10">
                                                                    <Input
                                                                        type="text"
                                                                        value={measure.name}
                                                                        onChange={(e) => updateControlMeasureName(measure.id, e.target.value)}
                                                                        placeholder="Control measure name"
                                                                    />
                                                                </Col>
                                                                <Col xs="2">
                                                                    <Button
                                                                        color="danger"
                                                                        outline
                                                                        onClick={() => removeControlMeasure(measure.id)}
                                                                        block
                                                                    >
                                                                        <i className="fas fa-trash"></i>
                                                                    </Button>
                                                                </Col>
                                                            </Row>

                                                            <div className="mt-3">
                                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                                    <Label className="mb-0">
                                                                        <strong>Options</strong>
                                                                    </Label>
                                                                    <Button
                                                                        color="success"
                                                                        outline
                                                                        onClick={() => addOption(measure.id)}
                                                                    >
                                                                        <i className="fas fa-plus"></i> Add Option
                                                                    </Button>
                                                                </div>
                                                                {measure.options && measure.options.length > 0 ? (
                                                                    measure.options.map((option, optIndex) => (
                                                                        <Row key={optIndex} className="mb-2">
                                                                            <Col xs="10">
                                                                                <Input
                                                                                    type="text"
                                                                                    value={option}
                                                                                    onChange={(e) => updateOption(measure.id, optIndex, e.target.value)}
                                                                                    placeholder={`Option ${optIndex + 1}`}
                                                                                    bsSize="sm"
                                                                                />
                                                                            </Col>
                                                                            <Col xs="2">
                                                                                <Button
                                                                                    color="danger"
                                                                                    outline
                                                                                    onClick={() => removeOption(measure.id, optIndex)}
                                                                                    block
                                                                                >
                                                                                    <i className="fas fa-times"></i>
                                                                                </Button>
                                                                            </Col>
                                                                        </Row>
                                                                    ))
                                                                ) : (
                                                                    <p className="text-muted mb-0">No options defined</p>
                                                                )}
                                                            </div>
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-3">
                                                <p className="text-muted mb-3">No control measures defined</p>
                                                <Button color="success" onClick={addControlMeasure}>
                                                    <i className="fas fa-plus"></i> Add First Control Measure
                                                </Button>
                                            </div>
                                        )}
                                    </CardBody>
                                </Collapse>
                            </Card>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    </>;
})